import {
  Trimester,
  TrimesterPlan,
} from "../interfaces/educationLibraryInterfaces";
import {
  Trimester as CarePlanTrimester,
  TrimesterPlan as CarePlanTrimesterPlan,
  ActivityData,
  BackendResponse,
  TrimesterData,
  Task,
  TaskStatus,
  CarePlan,
  Reasons,
} from "../interfaces/carePlanInterfaces";
import imageMap, { ImageKey } from "./ImageMap";

import {
  CategoryWiseStatus,
  EducationalContentType,
  GroupedCategoryStatus,
  DisplayGroup,
  MilestoneType,
  OptReasons,
} from "../interfaces/masterDataInterfaces";
import {
  OPT_OUT_REASON_MSG_TYPE,
  PATIENT_NO_SHOW_REASON_MSG_TYPE,
} from "../constants/common-constants";
import { RootState, store } from "../../store/store";
import { invalidateEducationLibrary } from "../../features/education-library/services/GetEducationLibraryAPISlice";
import { enqueueSnackbar } from "notistack";
import { CategoryReason } from "../interfaces/onboardingDataInterfaces";
import { MRT_FilterFn } from "material-react-table";
import { MilestoneData } from "../../features/manage-milestones/ManageMilestones";
import i18n from "i18next";

const monthList = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// const inputDate = "2024-11-25T07:13:33.137";
// console.log(outputDate); // Outputs: "11/25/2024"

export function formatDate(dateString: string | null): string {
  if (!dateString) return "--";
  // Parse the input date string into a Date object
  const date = new Date(dateString);

  // Extract the month, day, and year
  const month = String(date.getMonth() + 1).padStart(2, "0"); // ensures two digits
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  // Format the date as MM/DD/YYYY
  const formattedDate = `${month}/${day}/${year}`;

  // Example usage

  return formattedDate;
}
// const inputDate = "2024-11-25T07:13:33.137";
// Outputs: Oct 25, 2024
export function formatDateMonthDateYear(dateString: string): string {
  // Parse the input date string into a Date object
  const date = new Date(dateString);

  // Extract the month, day, and year
  const month = date.getMonth(); // getMonth() returns 0-indexed month
  const day = date.getDate();
  const year = date.getFullYear();

  // Format the date as MM/DD/YYYY
  const formattedDate = `${monthList[month]} ${day}, ${year}`;

  // Example usage
  // const inputDate = "2024-10-25T07:13:33.137";
  // const outputDate = formatDate(inputDate);
  // console.log(outputDate); // Outputs: "Oct 25, 2024"

  return formattedDate;
}

export function formatDateInWordOutput(inputDate: string): string {
  // Create a Date object from the input string
  const date = new Date(inputDate);

  // Get day, month, and year
  const day = date.getDate();
  const month = monthList[date.getMonth()];
  const year = date.getFullYear();

  // Determine the ordinal suffix for the day
  const ordinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return "th"; // For dates 4-20
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };
  // Example usage:
  // const formattedDate = formatDate("2025-07-10T00:00:00");
  // console.log(formattedDate); // Output: "Jul 10th 2025"

  // Format the date as "Jul 10th 2025"
  return `${month} ${day}${ordinalSuffix(day)} ${year}`;
}

interface GroupedOption {
  group: string;
  options: Array<{ label: string; value: number }>;
}

export const formatMilestoneData = (data: DisplayGroup[]): GroupedOption[] => {
  const groupedData: { [key: string]: GroupedOption } = {};

  data.forEach((item) => {
    const group = item.trimesterCode || "General";
    if (!groupedData[group]) {
      groupedData[group] = { group, options: [] };
    }
    groupedData[group].options.push({
      label: item.displayGroupDisplayName,
      value: item.displayGroupId,
    });
  });

  return Object.values(groupedData);
};

export const formatEducationMilestoneData = (
  data: DisplayGroup[]
): GroupedOption[] => {
  const groupedData: { [key: string]: GroupedOption } = {};

  data.forEach((item) => {
    const group = item.trimesterCode || "General";
    if (
      group === "General" &&
      !(item.isMilestone === 0 && item.isDisplayGroupActive)
    ) {
      return;
    }
    if (!groupedData[group]) {
      groupedData[group] = { group, options: [] };
    }
    groupedData[group].options.push({
      label: item.displayGroupDisplayName,
      value: item.displayGroupId,
    });
  });

  return Object.values(groupedData);
};

// New function to format task types
export const formatTaskTypes = (
  taskTypes: MilestoneType[]
): { label: string; value: number }[] => {
  return taskTypes
    .filter((taskType) => taskType.milestoneTypeFilter === "Care Plan")
    .map((taskType) => ({
      label: taskType.milestoneTypeCode,
      value: taskType.milestoneTypeId,
    }));
};

export const formatEducationTaskTypes = (
  taskTypes: MilestoneType[]
): { label: string; value: number }[] => {
  return taskTypes
    .filter(
      (taskType) => taskType.milestoneTypeFilter === "Educational Content"
    )
    .map((taskType) => ({
      label: taskType.milestoneTypeCode,
      value: taskType.milestoneTypeId,
    }));
};

// segregate data based on category
export const segregateCategoryWiseStatus = (
  data: CategoryWiseStatus[]
): GroupedCategoryStatus[] => {
  const groupedData: { [key: number]: GroupedCategoryStatus } = {};

  data.forEach((item) => {
    if (!groupedData[item.categoryId]) {
      groupedData[item.categoryId] = {
        categoryId: item.categoryId,
        categoryCode: item.categoryCode,
        categoryDisplayName: item.categoryDisplayName,
        statuses: [],
      };
    }
    groupedData[item.categoryId].statuses.push({
      statusId: item.statusId,
      statusCode: item.statusCode,
      statusDisplayName: item.statusDisplayName,
    });
  });

  return Object.values(groupedData);
};

// Function to get dropdown data for a specific categoryId
export const getDropdownDataForCategory = (
  groupedCategoryStatus: GroupedCategoryStatus[],
  categoryId: number,
  isDraft: boolean = true
): { label: string; value: number }[] => {
  const category = groupedCategoryStatus.find(
    (group) => group.categoryId === categoryId
  );

  if (!category) {
    return [];
  }

  return category.statuses
    .filter((status) => (isDraft ? true : status.statusId !== 9))
    .map((status) => ({
      label: status.statusDisplayName,
      value: status.statusId,
    }));
};

export const getStatusIDFromStatusName = (
  categoryId: number,
  statusId: number,
  categoryWiseStatus: CategoryWiseStatus[]
) => {
  const groupedCategoryStatus = segregateCategoryWiseStatus(categoryWiseStatus);
  const statusDropdownData = getDropdownDataForCategory(
    groupedCategoryStatus,
    categoryId
  );

  // Find the status with the given statusId
  const matchedStatus = statusDropdownData.find(
    (status) => status.value === statusId
  );

  // Return the statusId of the matched status if found, otherwise return null
  return matchedStatus ? matchedStatus.value : null;
};

// New function to format content types, need to update the function **
export const formatContentTypes = (
  contentTypes: EducationalContentType[]
): { label: string; value: number }[] => {
  return contentTypes.map((contentType) => ({
    label: contentType.contentTypeDisplayName,
    value: contentType.educationalContentTypeId,
  }));
};

export const convertDisplayGroupsToLabelValue = (data: any) => {
  const result: any[] = [];
  data.forEach((trimester: any) => {
    const trimesterLabel =
      trimester.trimesterDisplayName || "General Education";
    trimester.trimesterPlans.forEach((plan: any) => {
      result.push({
        label: `${trimesterLabel} - ${plan.displayGroupDisplayName}`,
        value: plan.displayGroupDisplayName,
      });
    });
  });

  return result;
};

export const getStatusColorForCarePlanAccordianSummary = (status: string) => {
  switch (status) {
    case "Current":
      return { backgroundColor: "#00B6C74D" };
    case "Past":
      return { backgroundColor: "#F2EEE8" };
    case "Future":
      return { backgroundColor: "#FFFFFF", border: "1px solid #BCA685" };
    default:
      return { backgroundColor: "#F2EEE8" };
  }
};

// Utility to get the first "In Progress" group
export const getInitialGroup = (data: Trimester[]): string => {
  return (
    data
      .flatMap((trimester) => trimester.trimesterPlans)
      .find((plan) => plan.weekType === "Current")?.displayGroupDisplayName ||
    ""
  );
};

export const getInitialGroupCarePlan = (data: CarePlanTrimester[]): string => {
  return (
    data
      .flatMap((trimester) => trimester.trimesterPlans)
      .find((plan) => plan.groupProgressStatus === "In Progress")
      ?.displayGroupDisplayName || ""
  );
};

// Utility to find a plan by group name
export const findPlanByGroupName = (
  data: Trimester[],
  groupName: string
): TrimesterPlan | undefined => {
  return data
    .flatMap((trimester) => trimester.trimesterPlans)
    .find((plan) => plan.displayGroupDisplayName === groupName);
};

export const findPlanByGroupNameCarePlan = (
  data: CarePlanTrimester[],
  groupName: string
): CarePlanTrimesterPlan | undefined => {
  return data
    .flatMap((trimester) => trimester.trimesterPlans)
    .find((plan) => plan.displayGroupDisplayName === groupName);
};

export const getImage = (key: ImageKey, fallbackUrl?: string): string => {
  return fallbackUrl || imageMap[key];
};

export function countNotCompletedActivities(
  activities: ActivityData[],
  groupProgressStatus: string
): number {
  let count = 0;
  if (groupProgressStatus.toLowerCase() === "task pending") {
    for (const activity of activities) {
      if (activity.activityCompletionStatus.toLowerCase() === "pending") {
        count++;
      }
    }
  }

  return count;
}

// construct data for reward milestone page
export const structureCarePlanData = (
  data: BackendResponse
): TrimesterData[] => {
  return data.patientPlansInfo.map((trimester) => {
    const taskGroups = trimester.trimesterPlans.map((group) => {
      const tasks: Task[] = group.activities.map((activity) => ({
        name: activity.activityDisplayName,
        status: activity.activityCompletionStatus as TaskStatus,
        points: activity.activityPoints || 0,
      }));

      return {
        groupName: group.displayGroupDisplayName,
        points: group.accumulatedPointsByGroup || 0,
        weekType: group.weekType,
        tasks,
      };
    });

    const totalTrimesterPoints = taskGroups.reduce(
      (total, group) => total + group.points,
      0
    );

    return {
      trimesterDisplayName: trimester.trimesterDisplayName,
      points: totalTrimesterPoints,
      taskGroups,
      trimesterCurrentStatus: trimester.trimesterCurrentStatus,
    };
  });
};

export const getIdFromEducationContentType = (
  educationContentType: EducationalContentType[],
  contentTypeDisplayName: string
): number | null => {
  const matchingContentType = educationContentType.find(
    (content) => content.contentTypeDisplayName === contentTypeDisplayName
  );

  return matchingContentType
    ? matchingContentType.educationalContentTypeId
    : null;
};

// get max points from patients redemptions data
export const getMaxPoints = (redemptions: any[]) => {
  const tier4Redemption = redemptions.find(
    (redemption) => redemption.tierClassificationId === 4
  );
  return tier4Redemption ? tier4Redemption.rangeFrom : 0;
};

export const getHeadersFromRedux = () => {
  const state: RootState = store.getState();
  const accessToken = state.patientAuth?.accessToken;
  const patientFhirId = state.patientAuth?.patientFhirId;
  const patientProfileInfo = state.patientProfileDetails.patientProfileInfo;
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (patientFhirId) headers.patientFhirId = patientFhirId;
  if (patientProfileInfo?.preferredLanguage)
    headers["X-Language"] = patientProfileInfo?.preferredLanguage;
  return headers;
};

export const handleExternalLinkClick = async (
  casePlanMilestoneMappingId: string,
  CONTENT_STATUS_COMPLETED_ID: number,
  patientUserName: string | undefined,
  patientProfileId: number | undefined,
  lang: string | undefined,
  dispatch: any,
  saveContentStatus: any
) => {
  try {
    const body = {
      casePlanMilestoneMappingId,
      statusId: CONTENT_STATUS_COMPLETED_ID,
      updatedBy: patientUserName || "Patient",
    };
    await saveContentStatus({ body }).unwrap();

    if (patientProfileId) {
      invalidateEducationLibrary(dispatch, patientProfileId, lang!);
    }
    enqueueSnackbar(i18n.t("viewLearningContent.successStausMilestone"), {
      variant: "success",
    });
  } catch (err: any) {
    enqueueSnackbar("Error in saving the update", { variant: "error" });
  }
};

export const convertReasonsToLabelValue = (reasons: OptReasons[]) => {
  return reasons.map((reason) => ({
    label: reason.displayReason,
    value: reason.reasonId,
  }));
};

export const getPatientStatusTrasitionsOptions = (
  data: { label: string; value: number }[],
  valuesArray: number[],
  singleValue: number
): { label: string; value: number }[] => {
  const filterValues = new Set([...valuesArray, singleValue]);
  return data.filter((item) => filterValues.has(item.value));
};

export const getOptOutMessages = (messages: Reasons[]) => {
  return messages.filter(
    (message) => message.categoryCode === OPT_OUT_REASON_MSG_TYPE
  );
};

export const getPatientRoute = (
  _patientProfileId: string | number | null,
  subPath = ""
): string => `/patient${subPath}`;

export const filterByLanguages: MRT_FilterFn<MilestoneData> = (
  row,
  columnId,
  filterValue
) => {
  const enabledLanguages = row.original.enabledLanguages;
  if (!enabledLanguages || !Array.isArray(enabledLanguages)) return false;
  const filterValueLower = filterValue.toLowerCase();
  return enabledLanguages.some(
    (lang) =>
      (lang.languageName.toLowerCase().includes(filterValueLower) ||
        lang.languageCode.toLowerCase().includes(filterValueLower)) &&
      lang.active
  );
};

export const videoCompletionMessage = (points: number) => {
  return `Congratulations on completing the video! Please click 'complete' to update your progress and earn ${points} points`;
};

export const getJwtExpiry = (token: string): number | null => {
  try {
    const payloadBase64 = token.split(".")[1]; // Extract payload (middle part)
    const decodedPayload = JSON.parse(atob(payloadBase64)); // Decode base64
    return decodedPayload.exp;
  } catch (error) {
    return null;
  }
};

export const isTouchDevice = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(pointer: coarse)").matches;

export const getNoShowReasons = (
  reasons: CategoryReason[]
): CategoryReason[] => {
  return reasons.filter(
    (reason) => reason.categoryCode === PATIENT_NO_SHOW_REASON_MSG_TYPE
  );
};
export const getNameInitials = (name: string): string => {
  return name
    .split(" ")
    .map((name) => name.trim()[0])
    .join("")
    .toUpperCase();
};

export const formatDisplayDate = (
  dateString: string,
  locale: string = "en-US",
  monthFormat: "numeric" | "2-digit" | "long" | "short" | "narrow" = "long",
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: monthFormat,
    day: "numeric",
  }
): string => {
  const safeLocale = locale || "en-US";

  try {
    return new Date(dateString).toLocaleDateString(safeLocale, options);
  } catch (err) {
    console.error("Invalid date or locale:", { dateString, safeLocale, err });
    return dateString;
  }
};

export const capitalizeFirstLetter = (text: string): string => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const getCurrentCarePlan = (
  data?: CarePlan
): CarePlanTrimesterPlan | null => {
  if (!data) return null;

  for (const trimester of data.patientPlansInfo) {
    const current = trimester.trimesterPlans.find(
      (plan) => plan.weekType === "Current"
    );
    if (current) return current;
  }
  return null;
};

export const tabHeaderBgColorMap: Record<number | string, string> = {
  2: "#a9c2b1",
  3: "#f2f0ec",
  4: "#eaf0f0",
  5: "#f2f0ec",
  profile: "#4e7e6e",
};

export const routeToGlobalMenuId: Record<
  number,
  (profileID: number | null) => string
> = {
  1: (id) => getPatientRoute(id), // My Dashboard
  2: (id) => getPatientRoute(id, "/care-plan"), // My Care Plan
  3: (id) => getPatientRoute(id, "/ultrasound"), // My Ultrasound
  4: (id) => getPatientRoute(id, "/my-reward-activity"), // My Rewards
  5: (id) => getPatientRoute(id, "/learning"), // My Learning
};

const defaultPriorityMap: Record<string, number> = {
  Visit: 1,
  Order: 2,
  Education: 3,
};

export const sortActivitiesByType = (
  activities: ActivityData[],
  priorityMap: Record<string, number> = defaultPriorityMap
): ActivityData[] => {
  return [...activities].sort((a, b) => {
    const aPriority = priorityMap[a.activityType] ?? 999;
    const bPriority = priorityMap[b.activityType] ?? 999;
    return aPriority - bPriority;
  });
};

export const getContentDuration = (length: number, videoDuration: number) =>  {
  if (length != null && length > 0) {
    return length;
  }
  if (videoDuration != null && videoDuration > 0) {
    return videoDuration;
  }
  return 0;
};

export const getPageNameFromPath = (path: string): string => {
  if (!path) return "Unknown";

  const lowerPath = path.toLowerCase();

  if (/\/patient(\/\d+)?\/care-plan/.test(lowerPath)) return "Care Plan";
  if (/\/patient(\/\d+)?\/ultrasound/.test(lowerPath)) return "Ultrasound";
  if (/\/patient(\/\d+)?\/my-reward-activity/.test(lowerPath)) return "Rewards";
  if (/^\/patient(\/\d+)?\/?$/.test(lowerPath)) return "Home";
  if (/\/patient(\/\d+)?\/learning/.test(lowerPath)) return "Learning";
  if (/\/patient(\/\d+)?\/user-consent/.test(lowerPath)) return "User Consent";
  if (/\/patient(\/\d+)?\/profile/.test(lowerPath)) return "Profile";
  if (/\/patient(\/\d+)?\/faq/.test(lowerPath)) return "FAQ";

  return "Other";
};



